import { useTranslations } from 'next-intl';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import { ShowingDto, ShowingGroupedByDateTimeDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { CountryCode } from '@cineamo/api/types/CountryCode';

import {
    ID_CINETECH_AHAUS,
    ID_CINETECH_EMSDETTEN,
    ID_CINETECH_GRONAU,
    ID_CINETECH_RHEINE
} from '@cineamo/api/constants/cinema-ids';
import { Client } from '@cineamo/api/constants/Client.enum';

import { getContentBackdropImage, getContentPosterImage } from '@cineamo/api/helpers/content-helper';
import {
    getMovieBackdropBlurDataImage,
    getMovieBackdropImage,
    getMovieOverview,
    getMoviePosterBlurDataImage,
    getMoviePosterImage,
    getMovieReleaseYear,
    getMovieRuntime,
    getMovieTitle,
    MovieImageOptions,
    MovieVersionDto
} from '@cineamo/api/helpers/movie-helper';

import { loadAsset } from './asset-helper';
import {
    convertIso6393ToIso6391,
    getLanguageNameFromLocale,
    ISO_639_1_LOCALE,
    ISO_639_3_LOCALE,
    ISO_3166_1_LOCALE
} from './locale-helper';

export type ShowingImageFilterOptions = {
    language?: ISO_639_1_LOCALE;
    countryCode?: ISO_3166_1_LOCALE;
    useBackdropAsFallback?: boolean;
    usePosterAsFallback?: boolean;
    usePlaceholder?: boolean;
    movieImageOptions?: MovieImageOptions;
};

const AlternativePosterImage = loadAsset('/images/placeholders/portrait/im-posterFilmPortrait.jpg');
const AlternativeBackdropImage = loadAsset('/images/placeholders/landscape/im-posterFilmLandscape.jpg');

export function getShowingName(
    showing?: ShowingDto,
    movie?: MovieVersionDto | null,
    locale?: string,
    countryCode?: ISO_3166_1_LOCALE
): string {
    // Movie title has priority when a movie exists, then fall back to event-request name,
    // content name and finally the showing's own name (mirrors the cinema-website app).
    // `showing` is optional (grouped list items resolve it lazily), so guarantee a string.
    return (
        getMovieTitle(movie, locale, countryCode) ||
        showing?._embedded?.eventRequest?.name ||
        showing?._embedded?.content?.name ||
        showing?.name ||
        ''
    );
}

export function getShowingDescription(
    showing: ShowingDto,
    movie: MovieVersionDto | null | undefined,
    locale: string
): string {
    return (
        getMovieOverview(movie, locale) ??
        showing?._embedded?.eventRequest?.description ??
        showing?._embedded?.content?.description
    );
}

// `showing`/`movie` are optional: callers read them straight out of a query that may not have
// resolved yet, and every access below is already guarded.
export function getShowingPoster(
    showing?: ShowingDto,
    movie?: MovieVersionDto | null,
    options?: ShowingImageFilterOptions
): string | null {
    const {
        language,
        countryCode,
        useBackdropAsFallback = false,
        usePlaceholder = true,
        movieImageOptions
    } = options || {};

    const showingEventRequest = showing?._embedded?.eventRequest;
    const showingContent = showing?._embedded?.content;
    let imageUrl = showingContent?.posterImageUrl ?? showing?.imageUrl;

    if (movie) {
        imageUrl = getMoviePosterImage(movie, {
            language,
            countryCode,
            useBackdropAsFallback,
            usePlaceholder: false,
            imageOptions: movieImageOptions
        });
    }
    if (!imageUrl && showingEventRequest) {
        imageUrl = showingEventRequest.imageUrl;
    }
    if (!imageUrl && showingContent) {
        imageUrl = getContentPosterImage(showingContent, undefined, {
            language,
            countryCode,
            useBackdropAsFallback,
            usePlaceholder: false
        });
    }
    return imageUrl ?? (usePlaceholder ? AlternativePosterImage : null);
}

export function getShowingPosterBlurDataImage(
    showing?: ShowingDto,
    movie?: MovieVersionDto | null,
    options?: ShowingImageFilterOptions
) {
    const { language, countryCode, useBackdropAsFallback = false, usePlaceholder = true } = options || {};

    const showingEventRequest = showing?._embedded?.eventRequest;
    const showingContent = showing?._embedded?.content;
    let imageUrl = showingContent?.posterImageUrl ?? showing?.imageUrl;

    if (movie) {
        imageUrl = getMoviePosterBlurDataImage(movie, {
            language,
            countryCode,
            useBackdropAsFallback,
            usePlaceholder: false
        });
    }
    if (!imageUrl && showingEventRequest) {
        imageUrl = showingEventRequest.imageUrl;
    }
    if (!imageUrl && showingContent) {
        imageUrl = getContentPosterImage(showingContent, undefined, {
            language,
            countryCode,
            useBackdropAsFallback,
            usePlaceholder: false
        });
    }
    return imageUrl ?? (usePlaceholder ? AlternativePosterImage : null);
}

export function getShowingBackdrop(
    showing: ShowingDto,
    movie: MovieVersionDto | null | undefined,
    options?: ShowingImageFilterOptions
): string | null {
    const {
        language,
        countryCode,
        usePosterAsFallback = false,
        usePlaceholder = true,
        movieImageOptions
    } = options || {};

    const showingEventRequest = showing?._embedded?.eventRequest;
    const showingContent = showing?._embedded?.content;
    let imageUrl = showingContent?.backdropImageUrl ?? showing?.imageUrl;

    if (movie) {
        imageUrl = getMovieBackdropImage(movie, {
            language,
            countryCode,
            usePosterAsFallback,
            usePlaceholder: false,
            imageOptions: movieImageOptions
        });
    }
    if (!imageUrl && showingEventRequest) {
        imageUrl = showingEventRequest.imageUrl;
    }
    if (!imageUrl && showingContent) {
        imageUrl = getContentBackdropImage(showingContent, undefined, {
            language,
            countryCode,
            usePosterAsFallback,
            usePlaceholder: false
        });
    }
    return imageUrl ?? (usePlaceholder ? AlternativeBackdropImage : null);
}

export function getShowingBackdropBlurDataImage(
    showing?: ShowingDto,
    movie?: MovieVersionDto | null,
    options?: ShowingImageFilterOptions
) {
    const { language, countryCode, usePosterAsFallback = false, usePlaceholder = true } = options || {};

    const showingEventRequest = showing?._embedded?.eventRequest;
    const showingContent = showing?._embedded?.content;
    let imageUrl = showingContent?.backdropImageUrl ?? showing?.imageUrl;

    if (movie) {
        imageUrl = getMovieBackdropBlurDataImage(movie, {
            language,
            countryCode,
            usePosterAsFallback,
            usePlaceholder: false
        });
    }
    if (!imageUrl && showingEventRequest) {
        imageUrl = showingEventRequest.imageUrl;
    }
    if (!imageUrl && showingContent) {
        imageUrl = getContentBackdropImage(showingContent, undefined, {
            language,
            countryCode,
            usePosterAsFallback,
            usePlaceholder: false
        });
    }
    return imageUrl ?? (usePlaceholder ? AlternativeBackdropImage : null);
}

export function getShowingDuration(
    showing: ShowingDto | undefined,
    movie: MovieVersionDto | null | undefined,
    locale: string,
    fallback = 0
): number {
    const showingContent = showing?._embedded?.content;
    const showingEventRequest = showing?._embedded?.eventRequest;

    return showingEventRequest?.duration || showingContent?.duration || getMovieRuntime(movie, locale) || fallback;
}

export function getShowingsProperties(showing: ShowingDto, isMobile?: boolean, includeLanguage?: boolean): string[] {
    const properties: string[] = [];

    if (!showing.isRegularShowtime) {
        properties.push(!isMobile ? 'CINEAMO EVENT' : 'CINEAMO');
    }
    if (showing.isOriginalLanguage && includeLanguage !== false) {
        properties.push('OV');
    }
    if (showing.isThreeDimensional) {
        properties.push('3D');
    } else {
        properties.push('2D');
    }
    if (showing.isDolbyAtmos) {
        properties.push('ATMOS');
    }

    return properties;
}

export function getShowingGroupProperties(showing: ShowingGroupedByDateTimeDto): string[] {
    const properties: string[] = [];

    // if (!showing.isRegularShowtime) {
    //     properties.push(!isMobile ? 'CINEAMO EVENT' : 'CINEAMO');
    // }
    if (showing.isOriginalLanguage) {
        if (showing.isOriginalLanguage) {
            const ovProperty = ['OV', convertIso6393ToIso6391(showing.language as ISO_639_3_LOCALE)]
                .filter(Boolean)
                .join(' ');
            properties.push(ovProperty);
        }
    }
    if (showing.isThreeDimensional) {
        properties.push('3D');
    } else {
        properties.push('2D');
    }

    return properties;
}

export function getShowingPropertiesDimension(showing: ShowingDto | ShowingGroupedByDateTimeDto): string | null {
    switch (showing.isThreeDimensional) {
        case true:
            return '3D';
        default:
            return null;
    }
}

/**
 * Returns formatted language info.
 * @example
 * ```ts
 * [
 *  'OmU',
 *  '🔊 EN',
 *  '💬 DE'
 * ]
 * ```
 *
 * @param showing
 * @param locale
 * @param options
 */
export function getShowingPropertiesLanguage(
    showing: ShowingDto | ShowingGroupedByDateTimeDto,
    locale: string,
    options?: { languageFormat?: 'short' | 'long' }
): string[] {
    const language = getShowingLanguages(showing, locale, options);
    const parts: string[] = [];

    if (language.languageInfo) {
        parts.push(language.languageInfo);
    }
    if (language.audioLanguage) {
        parts.push(
            `🔊 ${options?.languageFormat === 'long' ? language.audioLanguage : language.audioLanguage.toUpperCase()}`
        );
    }
    if (language.subtitleLanguage) {
        parts.push(
            `💬 ${options?.languageFormat === 'long' ? language.subtitleLanguage : language.subtitleLanguage.toUpperCase()}`
        );
    }

    return parts;
}

/**
 * Returns formatted language info.
 * @example
 * ```ts
 * {
 *  languageInfo: 'OmU',
 *  audioLanguage: 'EN',
 *  subtitledLanguage: 'DE'
 * }
 * ```
 *
 * or with options.languageFormat === 'long'
 * ```ts
 * {
 *  languageInfo: 'OmU',
 *  audioLanguage: 'English',
 *  subtitledLanguage: 'German'
 * }
 * ```
 *
 * @param showing
 * @param locale
 * @param options
 *
 * @deprecated Use {@link getShowingTicketLanguages} for the program/ticket UI — it is a 1:1 port of
 * the mobile app's `getLanguages` (matching OmU/OV/subtitle semantics). This variant additionally
 * emits a 'UT'/'Sub' info label for subtitled-but-not-original showings and compares the locale
 * against the ISO-639-1 language, which diverges from the mobile behavior.
 */
export function getShowingLanguages(
    showing: ShowingDto | ShowingGroupedByDateTimeDto,
    locale: string,
    options?: {
        languageFormat?: 'short' | 'long';
        languages?: Record<ISO_639_3_LOCALE, string>;
    }
): { audioLanguage?: string; subtitleLanguage?: string; languageInfo?: string } {
    const language: { audioLanguage?: string; subtitleLanguage?: string; languageInfo?: string } = {};

    const longLanguage =
        options?.languages?.[showing.language as ISO_639_3_LOCALE] ??
        getLanguageNameFromLocale(showing.language ?? '', locale);
    const longSubtitleLanguage =
        options?.languages?.[showing.subtitledLanguage as ISO_639_3_LOCALE] ??
        getLanguageNameFromLocale(showing.subtitledLanguage ?? '', locale);

    const iso6391Language = convertIso6393ToIso6391(showing.language as ISO_639_3_LOCALE);
    const iso6391SubtitleLanguage = convertIso6393ToIso6391(showing.subtitledLanguage as ISO_639_3_LOCALE);

    const finalLanguage = options?.languageFormat === 'long' ? longLanguage : iso6391Language;
    const finalSubtitleLanguage = options?.languageFormat === 'long' ? longSubtitleLanguage : iso6391SubtitleLanguage;

    // If isOriginalLanguage && isSubtitled it means the showing is in original language + subtitle (OmU)
    if (showing.isOriginalLanguage === true && showing.isSubtitled === true) {
        language.languageInfo = 'OmU';
        if (showing.language) {
            language.audioLanguage = finalLanguage;
        }
        if (showing.subtitledLanguage) {
            language.subtitleLanguage = finalSubtitleLanguage;
        }
    }
    // If isSubtitled but not in original language: a dubbed/non-original showing that still has subtitles.
    // Labelled UT (Untertitel) in German / Sub in English — distinct from OmU, which requires the original language.
    else if (showing.isSubtitled === true) {
        language.languageInfo = locale === 'en' ? 'Sub' : 'UT';
        if (showing.language) {
            language.audioLanguage = finalLanguage;
        }
        if (showing.subtitledLanguage) {
            language.subtitleLanguage = finalSubtitleLanguage;
        }
    }
    // If isOriginalLanguage && originalLanguage it means the showing is in original language (OV)
    else if (showing.isOriginalLanguage === true && locale !== iso6391Language) {
        language.languageInfo = 'OV';
        if (showing.language) {
            language.audioLanguage = finalLanguage;
        }
    }

    // Fallback if neither OmU nor OV, then just add the language like EN (if available)
    if (Object.keys(language).length === 0) {
        if (showing.language) {
            language.audioLanguage = finalLanguage;
        }
    }

    return language;
}

export type ShowingTicketLanguages = {
    audioLanguage: string | null;
    subtitleLanguage: string | null;
    languageInfo: string | null;
};

/**
 * Language info for the program ticket buttons — 1:1 port of the mobile app's
 * `getLanguages` (`packages/utils/src/showings-info-helper.ts`, SHORT format):
 * OmU (original + subtitled) → subtitled (audio/subtitle only, no info label) →
 * OV (original, locale differs) → fallback plain audio language. Short codes are
 * returned uppercase (e.g. 'EN'), exactly like the mobile helper.
 */
export function getShowingTicketLanguages(
    showing: ShowingDto | ShowingGroupedByDateTimeDto,
    locale: string | undefined
): ShowingTicketLanguages {
    const language: ShowingTicketLanguages = {
        audioLanguage: null,
        subtitleLanguage: null,
        languageInfo: null
    };

    function formatLanguage(languageCode: string): string {
        return convertIso6393ToIso6391(languageCode as ISO_639_3_LOCALE)?.toUpperCase() ?? '';
    }

    // Case 1: Original language with subtitles (OmU)
    if (showing.isOriginalLanguage && showing.isSubtitled) {
        language.languageInfo = 'OmU';
        if (showing.language) {
            language.audioLanguage = formatLanguage(showing.language);
        }
        if (showing.subtitledLanguage) {
            language.subtitleLanguage = formatLanguage(showing.subtitledLanguage);
        }
    }
    // Case 2: Subtitled but not original language (no info label, mirrors mobile)
    else if (showing.isSubtitled) {
        if (showing.language) {
            language.audioLanguage = formatLanguage(showing.language);
        }
        if (showing.subtitledLanguage) {
            language.subtitleLanguage = formatLanguage(showing.subtitledLanguage);
        }
    }
    // Case 3: Original language but locale differs (OV) — comparison kept verbatim from mobile
    else if (showing.isOriginalLanguage && locale !== showing.language) {
        language.languageInfo = 'OV';
        if (showing.language) {
            language.audioLanguage = formatLanguage(showing.language);
        }
    }

    // Fallback: just the audio language if nothing else matched
    if (!language.audioLanguage && !language.subtitleLanguage && !language.languageInfo) {
        if (showing.language) {
            language.audioLanguage = formatLanguage(showing.language);
        }
    }

    return language;
}

/**
 * @param duration - Duration in minutes
 * @param t - Translations
 * @returns {string} duration in format `DDh DDm` localized
 */
export function formatDuration(duration: number, t: ReturnType<typeof useTranslations>): string {
    const hours = Math.floor(duration / 60);
    const hoursStr = hours > 0 ? t('common.time.hour-short', { hours }) : '';
    const minutes = duration % 60;
    const minutesStr = minutes > 0 ? t('common.time.minute-short', { minutes }) : '';

    return [hoursStr, minutesStr].filter(Boolean).join(' ');
}

export function getShowingInfo(options: {
    showing?: ShowingDto;
    content?: ContentDto;
    movie?: MovieVersionDto | null;
    countryCode?: CountryCode;
    locale: string;
    t: ReturnType<typeof useTranslations>;
    showMovieDuration?: boolean;
    showGenres?: boolean;
    ageRatingText?: string;
}): Array<string> {
    const countryCode =
        options.countryCode ||
        options.content?._embedded?.cinema?.countryCode ||
        options.showing?._embedded?.cinema?.countryCode;
    const movie = options.movie;
    const movieReleaseYear = getMovieReleaseYear(movie, countryCode);

    const runtimeMinutes =
        (options.showMovieDuration && getMovieRuntime(movie, options.locale)) ||
        getShowingDuration(options.showing, movie as MovieVersionDto, options.locale) ||
        options.content?.duration ||
        options.showing?._embedded?.content?.duration;
    const runtimeFormatted = runtimeMinutes ? formatDuration(runtimeMinutes, options.t) : undefined;
    const genres = options.showGenres ? movie?.genres : [];

    return [
        movieReleaseYear,
        runtimeFormatted,
        (genres?.slice(0, 3) || []).map((genre) => options.t(`genres.tmdb-genre-${genre.id}`)).join(', '),
        options.ageRatingText
    ].filter((it): it is string => !!it);
}

export function getGroupedShowings(showings: ShowingDto[], client: Client): ShowingGroupedByDateTimeDto[] {
    const groupedShowings: ShowingGroupedByDateTimeDto[] = [];
    showings.forEach((showing) => {
        const showingDate = showing.startDatetime;
        const showingGroup = groupedShowings?.find((group) => {
            return (
                group.startDatetime === showing.startDatetime &&
                group.isOriginalLanguage === showing.isOriginalLanguage &&
                group.language === showing.language &&
                group.isThreeDimensional === showing.isThreeDimensional &&
                group.cinemaRoomId === showing.cinemaRoomId
            );
        });
        if (showingGroup) {
            showingGroup.showings.push(showing);
        } else {
            groupedShowings?.push({
                startDatetime: showingDate,
                cinemaRoomId: showing.cinemaRoomId,
                isThreeDimensional: showing.isThreeDimensional,
                language: showing.language,
                isOriginalLanguage: showing.isOriginalLanguage,
                originalLanguage: showing.originalLanguage,
                isDubbed: showing.isDubbed,
                dubbedLanguage: showing.dubbedLanguage,
                isSubtitled: showing.isSubtitled,
                subtitledLanguage: showing.subtitledLanguage,
                onlineTicketUrl: getOnlineTicketUrl(showing, client) ?? undefined,
                showings: [showing]
            });
        }
    });
    return groupedShowings;
}

export function getShowingTechnologies(showing: ShowingDto) {
    const technologies = {
        isImax: 'imax',
        isDolbyAtmos: 'dolby-atmos',
        isDolbyVision: 'dolby-vision',
        isDolbyCinema: 'dolby-cinema',
        is4DX: '4-dx',
        isScreenX: 'screen-x',
        isHFR: 'hfr'
    };

    return Object.entries(technologies)
        .filter(([key]) => showing[key as keyof ShowingDto])
        .map(([, value]) => value);
}

/**
 * Depending on the userLocale this fn will return true or false.
 * E.g. the showing `originalLanguage` is `en` and the userLocale is `en`, this fn will return `false`.
 * If the showing `originalLanguage` is `en` and the userLocale is `de`, this fn will return `true` because this
 * is what the user would expect to see.
 *
 * @param showing
 * @param cinema
 */
export function isOVShowing(showing: ShowingDto, cinema: CinemaDto): boolean {
    const locale = cinema?.countryCode
        ? // "und" = unknown/undefined language
          new Intl.Locale(`und-${cinema.countryCode}`)?.maximize()?.language
        : null;
    return !!showing.isOriginalLanguage && locale !== convertIso6393ToIso6391(showing.language as ISO_639_3_LOCALE);
}

export function getOnlineTicketUrl(showing: ShowingDto, client: Client): string | null {
    let ticketUrl: string | null | undefined = null;

    switch (client) {
        case Client.Web:
            ticketUrl = showing?.ticketUrls?.web;
            break;
        case Client.iOS:
            ticketUrl = showing?.ticketUrls?.ios;
            break;
        case Client.Android:
            ticketUrl = showing?.ticketUrls?.android;
            break;
        case Client.CinemaWebsite:
            ticketUrl = showing?.ticketUrls?.cinemaWebsite;
            break;
    }

    if (!ticketUrl) {
        ticketUrl = showing?.ticketUrls?.default ?? showing?.onlineTicketUrl;
    }

    const isCineamoOnlineTicketingEnabled =
        process.env.NEXT_PUBLIC_ENV_CIN_DOMAIN && ticketUrl?.includes(process.env.NEXT_PUBLIC_ENV_CIN_DOMAIN);

    if (isCineamoOnlineTicketingEnabled) {
        ticketUrl = `/booking/${showing.id}`;
    }

    if (isTicketInternationalUrl(showing)) {
        ticketUrl = `/booking/embedded/${showing.id}`;
    }

    return ticketUrl ?? null;
}

/**
 * If the showing is a Ticket International cinema, open the booking in an iframe.
 * @param showing
 */
function isTicketInternationalUrl(showing: ShowingDto) {
    switch (showing.cinemaId) {
        case ID_CINETECH_EMSDETTEN:
        case ID_CINETECH_AHAUS:
        case ID_CINETECH_GRONAU:
        case ID_CINETECH_RHEINE:
            return true;
        default:
            return false;
    }
}

export function getHoursUntilShowing(startDatetime: ShowingDto['startDatetime']): number | undefined {
    if (!startDatetime) {
        return undefined;
    }
    return Math.round((new Date(startDatetime).getTime() - new Date().getTime()) / 1000 / 60 / 60);
}

/**
 * Whether online ticket sale has not opened yet for a showing.
 *
 * The ticket system (e.g. Cinetixx) can define a sale-start (`startSaleDatetime`)
 * before which tickets must not be bookable. When this returns true the buy
 * button must be disabled and the user told when sale opens.
 */
export function isShowingSalePending(showing: ShowingDto): boolean {
    if (!showing.startSaleDatetime) {
        return false;
    }
    return new Date(showing.startSaleDatetime).getTime() > Date.now();
}
